import React, { useState } from 'react';
import { PencilIcon, PlusIcon, SearchIcon, Trash2Icon, XIcon } from 'lucide-react';
import { DISCOUNTS, DISCOUNT_STATUS_META } from '../../data/discounts';
import { Discount, DiscountType } from '../../types';
import { formatPrice } from '../../utils/format';
const fieldClass = 'w-full border border-white/10 bg-ink-900 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-pink-500 focus:outline-none';
const labelClass = 'block text-[10px] font-bold uppercase tracking-[0.18em] text-white/45';
function valueLabel(discount: Discount): string {
  return discount.type === 'percent' ? `${discount.value} %` : formatPrice(discount.value);
}
export function DiscountsSection() {
  const [discounts, setDiscounts] = useState<Discount[]>(DISCOUNTS);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Discount | null>(null);
  const [type, setType] = useState<DiscountType>('percent');
  const rows = discounts.filter(d => d.code.toLowerCase().includes(query.toLowerCase()));
  const openCreate = () => {
    setEditing(null);
    setType('percent');
    setModalOpen(true);
  };
  const openEdit = (discount: Discount) => {
    setEditing(discount);
    setType(discount.type);
    setModalOpen(true);
  };
  const remove = (id: string) => setDiscounts(prev => prev.filter(d => d.id !== id));
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const code = String(form.get('code') ?? '').trim().toUpperCase();
    if (!code) return;
    const payload = {
      code,
      type,
      value: Number(form.get('value') ?? 0),
      minPurchase: Number(form.get('min') ?? 0),
      limit: Number(form.get('limit') ?? 0),
      expiry: String(form.get('expiry') ?? ''),
      status: String(form.get('status') ?? 'activo') as Discount['status']
    };
    if (editing) {
      setDiscounts(prev => prev.map(d => d.id === editing.id ? {
        ...d,
        ...payload
      } : d));
    } else {
      setDiscounts(prev => [{
        id: `d${Date.now()}`,
        uses: 0,
        ...payload
      }, ...prev]);
    }
    setModalOpen(false);
  };
  return <>
      <section className="border border-white/8 bg-ink-700" data-id="element-297" data-mp="3hev0afrnp7vf">
        <div className="flex flex-col gap-4 border-b border-white/8 p-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-298" data-mp="3hh7np8g6oafv">
          <div data-id="element-299" data-mp="3rdan46jnhuz4">
            <h2 className="font-display text-2xl uppercase tracking-wide text-white" data-id="element-300" data-mp="3rcrh9o79yx1g">
              Descuentos
            </h2>
            <p className="mt-1 text-xs text-white/40" data-id="element-301" data-mp="8ayszq2dcb1">
              {discounts.filter(d => d.status === 'activo').length} códigos
              activos de {discounts.length}
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-302" data-mp="21zxgubrz9wvd">
            <div className="relative" data-id="element-303" data-mp="25z6cxnahq6u3">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" data-id="element-304" data-mp="2620tv4uap9ir" />
              <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Buscar código..." aria-label="Buscar código de descuento" className="w-full border border-white/10 bg-ink-900 py-2.5 pl-9 pr-3 text-sm uppercase text-white placeholder:text-white/30 focus:border-pink-500 focus:outline-none sm:w-52" data-id="element-305" data-mp="1tpp1tyijvlsa" />
            </div>
            <button type="button" onClick={openCreate} className="flex items-center justify-center gap-2 bg-pink-500 px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-pink-600" data-id="element-306" data-mp="2cgzo22dbzadu">
              <PlusIcon className="h-4 w-4" data-id="element-307" data-mp="x07kune4adba" /> Crear descuento
            </button>
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-308" data-mp="rys2x478kmx4">
          <table className="w-full min-w-[900px] text-left text-sm" data-id="element-309" data-mp="2fxazuvr3okvh">
            <thead data-id="element-310" data-mp="2fuy6znjt0q3n">
              <tr className="border-b border-white/8 text-[10px] uppercase tracking-[0.16em] text-white/40" data-id="element-311" data-mp="2knrdafux86hu">
                <th scope="col" className="px-5 py-3 font-semibold" data-id="element-312" data-mp="26wml1ytvughj">
                  Código
                </th>
                <th scope="col" className="px-5 py-3 font-semibold" data-id="element-313" data-mp="2ax6hf06mtg2m">
                  Tipo
                </th>
                <th scope="col" className="px-5 py-3 font-semibold" data-id="element-314" data-mp="2yolo155fyjjo">
                  Valor
                </th>
                <th scope="col" className="px-5 py-3 font-semibold" data-id="element-315" data-mp="33m3m20hx7ecw">
                  Compra mínima
                </th>
                <th scope="col" className="px-5 py-3 font-semibold" data-id="element-316" data-mp="2poyiq89lctlc">
                  Usos / Límite
                </th>
                <th scope="col" className="px-5 py-3 font-semibold" data-id="element-317" data-mp="1e7p35wtl2lpb">
                  Vencimiento
                </th>
                <th scope="col" className="px-5 py-3 font-semibold" data-id="element-318" data-mp="1j7bicbsz2c0r">
                  Estado
                </th>
                <th scope="col" className="px-5 py-3 text-right font-semibold" data-id="element-319" data-mp="1aoqb2cebkzuf">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody data-id="element-320" data-mp="1knp8eytmx924">
              {rows.map(discount => {
              const meta = DISCOUNT_STATUS_META[discount.status];
              const pct = discount.limit ? Math.min(100, discount.uses / discount.limit * 100) : 0;
              return <tr key={discount.id} className="border-b border-white/5 transition-colors last:border-0 hover:bg-white/[0.02]" data-id="element-321" data-mp="n073y070at3x">
                    <td className="px-5 py-3" data-id="element-322" data-mp="11qwceut31u43">
                      <span className="border border-pink-500/40 bg-pink-500/10 px-2.5 py-1 font-mono text-xs font-bold tracking-wider text-pink-400" data-id="element-323" data-mp="wxbmzr5k28gi">
                        {discount.code}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-white/60" data-id="element-324" data-mp="47v0190pfjro">
                      {discount.type === 'percent' ? 'Porcentaje' : 'Monto fijo'}
                    </td>
                    <td className="px-5 py-3 font-semibold text-white" data-id="element-325" data-mp="299gwvheigtd7">
                      {valueLabel(discount)}
                    </td>
                    <td className="px-5 py-3 text-white/60" data-id="element-326" data-mp="2i3c20pfmujsu">
                      {discount.minPurchase === 0 ? 'Sin mínimo' : formatPrice(discount.minPurchase)}
                    </td>
                    <td className="px-5 py-3" data-id="element-327" data-mp="1qd7bsa56z2jp">
                      <p className="text-white" data-id="element-328" data-mp="1qe0m1l3d0a6l">
                        {discount.uses}{' '}
                        <span className="text-white/35" data-id="element-329" data-mp="1ufvmkndgujjb">/ {discount.limit}</span>
                      </p>
                      <span className="mt-1.5 block h-1 w-24 bg-ink-600" aria-hidden data-id="element-330" data-mp="1zahntxpu2vpo">
                        <span className="block h-full bg-pink-500" style={{
                      width: `${pct}%`
                    }} data-id="element-331" data-mp="3aqpsc4otsh3z" />
                      </span>
                    </td>
                    <td className="px-5 py-3 text-white/60" data-id="element-332" data-mp="3fyxxpxz1yt6y">{discount.expiry}</td>
                    <td className="px-5 py-3" data-id="element-333" data-mp="3b4lardydazqg">
                      <span className={`border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] ${meta.className}`} data-id="element-334" data-mp="374sz2sdogdgv">
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-5 py-3" data-id="element-335" data-mp="2x8g7q15lubpa">
                      <div className="flex items-center justify-end gap-2" data-id="element-336" data-mp="2s6qx2mmao01q">
                        <button type="button" onClick={() => openEdit(discount)} aria-label={`Editar ${discount.code}`} className="border border-white/10 p-2 text-white/60 transition-colors hover:border-pink-500 hover:text-pink-500" data-id="element-337" data-mp="2o5ovhz4wnbwl">
                          <PencilIcon className="h-3.5 w-3.5" data-id="element-338" data-mp="15nvenifb9547" />
                        </button>
                        <button type="button" onClick={() => remove(discount.id)} aria-label={`Eliminar ${discount.code}`} className="border border-white/10 p-2 text-white/60 transition-colors hover:border-red-500 hover:text-red-500" data-id="element-339" data-mp="19o5gqjzzfbds">
                          <Trash2Icon className="h-3.5 w-3.5" data-id="element-340" data-mp="qyfpu7zcb9r5" />
                        </button>
                      </div>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>

        {rows.length === 0 && <div className="px-6 py-16 text-center" data-id="element-341" data-mp="27bw5hvl2byx7">
            <h3 className="font-display text-2xl uppercase text-white" data-id="element-342" data-mp="27d76xor1cul5">
              Sin códigos
            </h3>
            <p className="mx-auto mt-2 max-w-sm text-sm text-white/40" data-id="element-343" data-mp="2c9633yilvgw4">
              No encontramos códigos con ese nombre.
            </p>
          </div>}
      </section>

      {modalOpen && <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/75 p-4 sm:items-center" role="dialog" aria-modal="true" aria-labelledby="discount-modal-title" data-id="element-344" data-mp="2hejbz1wjohpb">
          <div className="w-full max-w-2xl border border-white/10 bg-ink-700" data-id="element-345" data-mp="354vt93dojqsk">
            <div className="flex items-center justify-between border-b border-white/8 px-6 py-5" data-id="element-346" data-mp="307dh744d8vgc">
              <h2 id="discount-modal-title" className="font-display text-2xl uppercase tracking-wide text-white" data-id="element-347" data-mp="3e7511lhkd629">
                {editing ? 'Editar descuento' : 'Crear descuento'}
              </h2>
              <button type="button" onClick={() => setModalOpen(false)} aria-label="Cerrar" className="p-1.5 text-white/50 transition-colors hover:text-white" data-id="element-348" data-mp="1jsvox7lzbbqg">
                <XIcon className="h-5 w-5" data-id="element-349" data-mp="2v8e8oxynde68" />
              </button>
            </div>

            <form onSubmit={handleSubmit} data-id="element-350" data-mp="2q91okkz5ul7c">
              <div className="grid gap-5 p-6 sm:grid-cols-2" data-id="element-351" data-mp="2qa4dwogu4fl5">
                <div className="sm:col-span-2" data-id="element-352" data-mp="2la89wp850o5l">
                  <label className={labelClass} htmlFor="d-code" data-id="element-353" data-mp="2lbjdanxo7tw6">
                    Código
                  </label>
                  <input id="d-code" name="code" required defaultValue={editing?.code} placeholder="ZADAR10" className={`mt-2 ${fieldClass} uppercase`} data-id="element-354" data-mp="3izth98h0orpx" />
                </div>

                <fieldset className="sm:col-span-2" data-id="element-355" data-mp="346zzthi6q1im">
                  <legend className={labelClass} data-id="element-356" data-mp="5jm2u4estmok">Tipo de descuento</legend>
                  <div className="mt-2 grid gap-3 sm:grid-cols-2" data-id="element-357" data-mp="rlvhqwefrhy">
                    {([{
                  id: 'percent',
                  label: 'Porcentaje (%)'
                }, {
                  id: 'fixed',
                  label: 'Monto fijo ($)'
                }] as {
                  id: DiscountType;
                  label: string;
                }[]).map(option => <label key={option.id} className={`flex cursor-pointer items-center gap-3 border p-3.5 text-sm transition-colors ${type === option.id ? 'border-pink-500 bg-pink-500/5 text-white' : 'border-white/10 bg-ink-900 text-white/60 hover:border-white/30'}`} data-id="element-358" data-mp="i3j1adrz3s57">
                        <input type="radio" name="type" checked={type === option.id} onChange={() => setType(option.id)} className="h-4 w-4 shrink-0 appearance-none rounded-full border border-white/25 checked:border-[5px] checked:border-pink-500 focus:outline-none" data-id="element-359" data-mp="15radzihfe770" />
                        {option.label}
                      </label>)}
                  </div>
                </fieldset>

                <div data-id="element-360" data-mp="3kmhlo8d9m1qr">
                  <label className={labelClass} htmlFor="d-value" data-id="element-361" data-mp="3fteta3k84yc5">
                    Valor
                  </label>
                  <input id="d-value" name="value" type="number" min={1} required defaultValue={editing?.value} placeholder={type === 'percent' ? '10' : '500'} className={`mt-2 ${fieldClass}`} data-id="element-362" data-mp="3uhrr762vgr9a" />
                </div>

                <div data-id="element-363" data-mp="294q4ekrtsnff">
                  <label className={labelClass} htmlFor="d-min" data-id="element-364" data-mp="29601p7qutrl0">
                    Compra mínima
                  </label>
                  <input id="d-min" name="min" type="number" min={0} defaultValue={editing?.minPurchase ?? 0} placeholder="2000" className={`mt-2 ${fieldClass}`} data-id="element-365" data-mp="255zaeminplwj" />
                </div>

                <div data-id="element-366" data-mp="1q8g0ljwhmtc0">
                  <label className={labelClass} htmlFor="d-limit" data-id="element-367" data-mp="1q7y6astdyp6q">
                    Límite de usos
                  </label>
                  <input id="d-limit" name="limit" type="number" min={1} defaultValue={editing?.limit ?? 100} placeholder="500" className={`mt-2 ${fieldClass}`} data-id="element-368" data-mp="1be36sdf4ec87" />
                </div>

                <div data-id="element-369" data-mp="2wp1uzqay7elq">
                  <label className={labelClass} htmlFor="d-expiry" data-id="element-370" data-mp="2wqmodclj0ri0">
                    Vencimiento
                  </label>
                  <input id="d-expiry" name="expiry" defaultValue={editing?.expiry ?? '31 dic 2026'} placeholder="31 dic 2026" className={`mt-2 ${fieldClass}`} data-id="element-371" data-mp="31mbslt8hqwkn" />
                </div>

                <div className="sm:col-span-2" data-id="element-372" data-mp="rq23rc0h2afs">
                  <label className={labelClass} htmlFor="d-status" data-id="element-373" data-mp="nn5fp5s5xm4g">
                    Estado
                  </label>
                  <select id="d-status" name="status" defaultValue={editing?.status ?? 'activo'} className={`mt-2 ${fieldClass}`} data-id="element-374" data-mp="io2n4ktdd8dd">
                    <option value="activo" data-id="element-375" data-mp="8veodkitwuc0">Activo</option>
                    <option value="pausado" data-id="element-376" data-mp="3zg0dy1exec4">Pausado</option>
                    <option value="vencido" data-id="element-377" data-mp="3w27ar2mm2g8y">Vencido</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-col gap-3 border-t border-white/8 px-6 py-5 sm:flex-row sm:justify-end" data-id="element-378" data-mp="3r2ughwteu6wc">
                <button type="button" onClick={() => setModalOpen(false)} className="border border-white/20 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-white/70 transition-colors hover:border-white hover:text-white" data-id="element-379" data-mp="26ka5eb3b7zmb">
                  Cancelar
                </button>
                <button type="submit" className="bg-pink-500 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-pink-600" data-id="element-380" data-mp="1nuu61r3d2d7h">
                  Guardar descuento
                </button>
              </div>
            </form>
          </div>
        </div>}
    </>;
}